import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { locale } from '../../../i18n/format';
import { affiliateAPI } from '../../../services/api';
import { fetchDatumPassStatus } from '../../datumPass/store/datumPassSlice';
import DatumPassOfferModal from '../../datumPass/components/DatumPassOfferModal';
import UserAvatar from '../../../shared/components/UserAvatar';
import Preloader from '../../../shared/components/Preloader';
import datumPassCrownImg from '../../../images/datumpass/bp_crown.png';
import '../styles/partnershipTab.css';

// ── Helpers ──────────────────────────────────────────────────

function fmtTon(val) {
  if (!val && val !== 0) return '0';
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n % 1 === 0 ? n.toString() : n.toFixed(4).replace(/\.?0+$/, '');
}

function fmtPct(val) {
  if (!val && val !== 0) return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : `${(n * 100).toFixed(0)}%`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtShortDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'short' });
}

function daysLeft(isoExpiry) {
  if (!isoExpiry) return 0;
  const diff = new Date(isoExpiry) - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

/** Level-1 values are the "headline" numbers: what you get from a directly invited friend. */
function headlineRates(levelConfigs) {
  const rows = levelConfigs || [];
  const dp = rows.find((r) => r.datumPassReward != null);
  const win = rows.find((r) => r.tonWinPercent != null);
  return {
    dpReward: dp ? fmtTon(dp.datumPassReward) : null,
    winPct: win ? fmtPct(win.tonWinPercent) : null,
    depth: rows.length,
  };
}

const LEVELS_COLLAPSED = 3;

// ── Icons (inline so they inherit the theme colours) ─────────

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.5l2.9 6.1 6.7.8-4.9 4.6 1.3 6.6L12 17.3l-6 3.3 1.3-6.6L2.4 9.4l6.7-.8L12 2.5z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="12" x2="20" y2="12" />
      <polyline points="14 6 20 12 14 18" />
    </svg>
  );
}

// ── Hero: DatumPass status + CTA ─────────────────────────────

function HeroCard({ status, depth, onBuyClick }) {
  const { t } = useTranslation();
  const isActive = !!status?.datumPassEnabled;
  const expiry = status?.expiresAt;
  const days = daysLeft(expiry);
  const expiringSoon = isActive && days <= 2;

  let tone = 'locked';
  if (isActive) tone = expiringSoon ? 'warning' : 'active';

  return (
    <div className={`pt-hero pt-hero--${tone}`}>
      <div className="pt-hero-glow" />
      <div className="pt-hero-top">
        <img src={datumPassCrownImg} alt="" className="pt-hero-crown" />
        <div className="pt-hero-text">
          <div className="pt-hero-title">{t('partnership.hero.title', 'Partnership Program')}</div>
          <div className="pt-hero-sub">
            {depth > 1
              ? t('partnership.hero.sub_depth', 'Earn GRAM from every friend you invite — up to {{depth}} levels deep.', { depth })
              : t('partnership.hero.sub', 'Earn GRAM from every friend you invite.')}
          </div>
        </div>
      </div>

      {isActive ? (
        <div className={`pt-status pt-status--${tone}`}>
          <span className="pt-status-icon"><CheckIcon /></span>
          <span className="pt-status-text">
            <strong>{t('partnership.status.active', 'DatumPass Active')}</strong>
            <span className="pt-status-meta">
              {expiringSoon
                ? (days === 0
                  ? t('partnership.status.expires_today', 'Expires today — renew to keep earning')
                  : t('partnership.status.days_left_renew', {
                    count: days,
                    defaultValue_one: '{{count}} day left — renew to keep earning',
                    defaultValue_other: '{{count}} days left — renew to keep earning',
                  }))
                : t('partnership.status.days_left_until', {
                  count: days,
                  date: fmtDate(expiry),
                  defaultValue_one: '{{count}} day left · until {{date}}',
                  defaultValue_other: '{{count}} days left · until {{date}}',
                })}
            </span>
          </span>
        </div>
      ) : (
        <div className="pt-status pt-status--locked">
          <span className="pt-status-icon"><LockIcon /></span>
          <span className="pt-status-text">
            <strong>{t('partnership.status.required', 'DatumPass required')}</strong>
            <span className="pt-status-meta">{t('partnership.status.required_hint', 'Rewards are paid only while your DatumPass is active')}</span>
          </span>
        </div>
      )}

      <button
        className={`app-btn ${isActive && !expiringSoon ? 'app-btn--purple' : 'app-btn--green'} pt-hero-btn`}
        onClick={onBuyClick}
      >
        {isActive ? t('partnership.renew', 'Renew DatumPass · +30 days') : t('partnership.get_pass', 'Get DatumPass')}
      </button>
    </div>
  );
}

// ── How you earn: two income streams + conditions ────────────

function EarnStreams({ rates, hasPass, eligibleFriends, directFriends }) {
  const { t } = useTranslation();
  return (
    <div className="pt-streams">
      {/* Stream 1: DatumPass sales */}
      <div className="pt-stream pt-stream--dp">
        <div className="pt-stream-flow">
          <div className="pt-stream-from">
            <img src={datumPassCrownImg} alt="" className="pt-stream-icon" />
            <span>{t('partnership.stream.friend_buys', 'Friend buys')}<br />DatumPass</span>
          </div>
          <span className="pt-stream-arrow"><ArrowIcon /></span>
          <div className="pt-stream-to">
            <span className="pt-stream-label">{t('partnership.stream.you_get', 'You get')}</span>
            <span className="pt-stream-value pt-stream-value--dp">
              {rates.dpReward != null ? (
                <>
                  <img src="/gram.png" alt="" className="pt-stream-gram" />
                  {rates.dpReward} GRAM
                </>
              ) : t('partnership.stream.gram_bonus', 'GRAM bonus')}
            </span>
            <span className="pt-stream-note">{t('partnership.stream.per_purchase', 'per purchase, every renewal')}</span>
          </div>
        </div>
        <div className="pt-stream-conds">
          <span className={`pt-cond ${hasPass ? 'pt-cond--ok' : 'pt-cond--locked'}`}>
            <span className="pt-cond-icon">{hasPass ? <CheckIcon /> : <LockIcon />}</span>
            {t('partnership.cond.own_pass', 'Your DatumPass is active')}
          </span>
        </div>
      </div>

      {/* Stream 2: share of friends' GRAM wins */}
      <div className="pt-stream pt-stream--win">
        <div className="pt-stream-flow">
          <div className="pt-stream-from">
            <img src="/gram.png" alt="" className="pt-stream-icon" />
            <span>{t('partnership.stream.friend_wins', 'Friend wins')}<br />GRAM</span>
          </div>
          <span className="pt-stream-arrow"><ArrowIcon /></span>
          <div className="pt-stream-to">
            <span className="pt-stream-label">{t('partnership.stream.you_get', 'You get')}</span>
            <span className="pt-stream-value pt-stream-value--win">
              {t('partnership.stream.of_the_win', '{{pct}} of the win', { pct: rates.winPct ?? '%' })}
            </span>
            <span className="pt-stream-note">{t('partnership.stream.every_win', 'from every GRAM win, no limit')}</span>
          </div>
        </div>
        <div className="pt-stream-conds">
          <span className={`pt-cond ${hasPass ? 'pt-cond--ok' : 'pt-cond--locked'}`}>
            <span className="pt-cond-icon">{hasPass ? <CheckIcon /> : <LockIcon />}</span>
            {t('partnership.cond.own_pass', 'Your DatumPass is active')}
          </span>
          <span className="pt-cond pt-cond--info">
            <span className="pt-cond-icon pt-cond-icon--star"><StarIcon /></span>
            {t('partnership.cond.friend_premium', 'Friend has Telegram Premium or DatumPass')}
          </span>
        </div>
        <div className="pt-stream-foot">
          <span className="pt-stream-foot-label">{t('partnership.friends_who_count', 'Friends who count')}</span>
          <span className="pt-stream-foot-value">
            <strong>{eligibleFriends}</strong> {t('partnership.of_total', 'of {{total}}', { total: directFriends })}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Earnings summary ──────────────────────────────────────────

function EarningsCard({ total, dpEarnings, tonEarnings }) {
  const { t } = useTranslation();
  return (
    <div className="pt-earn">
      <div className="pt-earn-total">
        <span className="pt-earn-total-label">{t('partnership.total_earned', 'Total earned')}</span>
        <span className="pt-earn-total-value">
          <img src="/gram.png" alt="GRAM" className="pt-earn-icon" />
          {fmtTon(total)} <span className="pt-earn-cur">GRAM</span>
        </span>
      </div>
      <div className="pt-earn-split">
        <div className="pt-earn-item pt-earn-item--dp">
          <span className="pt-earn-item-label">{t('partnership.earn.dp_sales', 'DatumPass Sales')}</span>
          <span className="pt-earn-item-value">{fmtTon(dpEarnings)} GRAM</span>
        </div>
        <div className="pt-earn-item pt-earn-item--win">
          <span className="pt-earn-item-label">{t('partnership.earn.win_rewards', 'Win Rewards')}</span>
          <span className="pt-earn-item-value">{fmtTon(tonEarnings)} GRAM</span>
        </div>
      </div>
    </div>
  );
}

// ── Levels table ──────────────────────────────────────────────

function CommissionTable({ levelConfigs }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  if (!levelConfigs || levelConfigs.length === 0) {
    return <div className="pt-empty">{t('partnership.table.empty', 'No commission configuration available')}</div>;
  }
  const rows = showAll ? levelConfigs : levelConfigs.slice(0, LEVELS_COLLAPSED);
  const hidden = levelConfigs.length - rows.length;

  return (
    <div className="pt-table-wrap">
      <table className="pt-table">
        <thead>
          <tr>
            <th>{t('partnership.table.level', 'Level')}</th>
            <th>DatumPass</th>
            <th>{t('partnership.table.win_share', 'Win share')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.level}>
              <td>
                <span className="pt-table-level-badge">{row.level}</span>
                {row.level === 1 && <span className="pt-table-level-hint">{t('partnership.table.your_friends', 'your friends')}</span>}
              </td>
              <td>
                {row.datumPassReward != null
                  ? <span className="pt-table-ton">{fmtTon(row.datumPassReward)} GRAM</span>
                  : <span className="pt-table-empty">—</span>}
              </td>
              <td>
                {row.tonWinPercent != null
                  ? <span className="pt-table-pct">{fmtPct(row.tonWinPercent)}</span>
                  : <span className="pt-table-empty">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(hidden > 0 || showAll) && (
        <button className="pt-table-toggle" onClick={() => setShowAll(!showAll)}>
          {showAll
            ? t('partnership.table.show_less', 'Show less')
            : t('partnership.table.show_all', 'Show all {{n}} levels', { n: levelConfigs.length })}
        </button>
      )}
    </div>
  );
}

// ── History ───────────────────────────────────────────────────

function HistoryList({ events }) {
  const { t } = useTranslation();
  if (!events || events.length === 0) {
    return <div className="pt-empty">{t('partnership.history.empty', 'No earnings yet — invite friends to start')}</div>;
  }
  return (
    <div className="pt-history-list">
      {events.map((e) => {
        const isDp = e.eventType === 'DATUM_PASS_PURCHASE';
        const typeLabel = isDp ? t('partnership.history.dp_sale', 'DatumPass Sale') : t('partnership.history.gram_win', 'GRAM Win');
        const detail = isDp
          ? t('partnership.history.dp_detail', 'Level {{level}} partner purchase', { level: e.level })
          : t('partnership.history.win_detail', 'Level {{level}} · won {{amount}} GRAM', { level: e.level, amount: fmtTon(e.amountSource) });
        return (
          <div className="pt-history-item" key={e.id}>
            <div className={`pt-history-badge ${isDp ? 'pt-history-badge--dp' : 'pt-history-badge--ton'}`}>
              L{e.level}
            </div>
            <div className="pt-history-info">
              <div className="pt-history-type">{typeLabel}</div>
              <div className="pt-history-detail">{detail}</div>
            </div>
            <div className="pt-history-right">
              <div className="pt-history-reward">+{fmtTon(e.rewardAmount)} GRAM</div>
              <div className="pt-history-date">{fmtShortDate(e.createdAt)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Friends with DatumPass ────────────────────────────────────

function FriendWithPassCard({ friend }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const name = [friend.firstName, friend.lastName].filter(Boolean).join(' ') || friend.username
    || t('common.anonymous', 'Anonymous');
  const levels = friend.partnerCountsByLevel || {};
  const levelKeys = Object.keys(levels).map(Number).sort((a, b) => a - b);

  return (
    <div className={`pt-friend-card ${expanded ? 'pt-friend-card--expanded' : ''}`}>
      <div className="pt-friend-header" onClick={() => setExpanded(!expanded)}>
        <UserAvatar name={name} size={36} className="avatar" />
        <div className="pt-friend-pass-dot" />
        <span className="pt-friend-name">{name}</span>
        <span className="pt-friend-expiry">{fmtShortDate(friend.expiresAt)}</span>
        <svg className="pt-friend-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {expanded && (
        <div className="pt-friend-levels">
          <div className="pt-friend-levels-title">{t('partnership.friend.by_depth', 'Partners by depth')}</div>
          {levelKeys.length > 0 ? (
            <div className="pt-level-rows">
              {levelKeys.map((lvl) => (
                <div className="pt-level-chip" key={lvl}>
                  <span className="pt-level-chip-depth">L{lvl}:</span>
                  <span className="pt-level-chip-count">{levels[lvl]}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="pt-friend-no-levels">{t('partnership.friend.no_sub', 'No sub-partners yet')}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function PartnershipTab() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isEnabled: dpEnabled, expiresAt: dpExpiry } = useSelector((s) => s.datumPass);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await affiliateAPI.getPartnership();
      setData(result);
    } catch (err) {
      console.error('Partnership load failed:', err);
      setError(t('partnership.load_failed', 'Failed to load partnership data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    dispatch(fetchDatumPassStatus());
    load();
  }, [dispatch, load]);

  const handleModalClose = useCallback(() => {
    setShowBuyModal(false);
    dispatch(fetchDatumPassStatus());
    load();
  }, [dispatch, load]);

  if (loading) {
    return <div className="pt-scroll"><Preloader /></div>;
  }

  if (error) {
    return (
      <div className="pt-scroll">
        <div className="pt-error">{error}</div>
        <button className="app-btn app-btn--purple" onClick={load} style={{ margin: '0 auto', display: 'block', width: 'calc(100% - 24px)' }}>{t('common.retry', 'Retry')}</button>
      </div>
    );
  }

  // Merge status: redux state is the freshest source; data.datumPassStatus is the initial load
  const passStatus = data?.datumPassStatus
    ? { datumPassEnabled: dpEnabled !== undefined ? dpEnabled : data.datumPassStatus.datumPassEnabled, expiresAt: dpExpiry || data.datumPassStatus.expiresAt }
    : { datumPassEnabled: dpEnabled || false, expiresAt: dpExpiry || null };

  const rates = headlineRates(data?.levelConfigs);
  const hasPass = !!passStatus.datumPassEnabled;

  return (
    <>
      <div className="pt-scroll">
        <HeroCard status={passStatus} depth={rates.depth} onBuyClick={() => setShowBuyModal(true)} />

        <div className="pt-section-title">{t('partnership.section.how', 'How you earn')}</div>
        <EarnStreams
          rates={rates}
          hasPass={hasPass}
          eligibleFriends={data?.eligibleFriendsCount ?? 0}
          directFriends={data?.directFriendsCount ?? 0}
        />

        <div className="pt-section-title">{t('partnership.section.total', 'Total Earnings')}</div>
        <EarningsCard
          total={data?.totalEarned ?? 0}
          dpEarnings={data?.datumPassEarnings ?? 0}
          tonEarnings={data?.tonWinEarnings ?? 0}
        />

        <div className="pt-section-title">{t('partnership.section.commission', 'Commission Structure')}</div>
        <CommissionTable levelConfigs={data?.levelConfigs} />

        <div className="pt-section-title">{t('partnership.section.history', 'Earnings History')}</div>
        <HistoryList events={data?.recentEvents} />

        {data?.friendsWithPass?.length > 0 && (
          <>
            <div className="pt-section-title">
              {t('partnership.section.partners', 'Partners with DatumPass ({{n}})', { n: data.friendsWithPass.length })}
            </div>
            <div className="pt-friends-list">
              {data.friendsWithPass.map((f, i) => (
                <FriendWithPassCard key={i} friend={f} />
              ))}
            </div>
          </>
        )}

        {!hasPass && (
          <div className="pt-bottom-cta">
            <button className="app-btn app-btn--green" onClick={() => setShowBuyModal(true)}>
              {t('partnership.cta_bottom', 'Get DatumPass & start earning')}
            </button>
          </div>
        )}
      </div>

      {showBuyModal && <DatumPassOfferModal onClose={handleModalClose} />}
    </>
  );
}
